import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';

const milestones = [
  { year: '2022 Q2', title: '项目启动', desc: '成91平台创始团队组建，开启平台设计与研发工作，确立了以用户体验为核心的产品理念。' },
  { year: '2023 Q1', title: '正式上线', desc: '成91平台1.0版本正式发布，首批用户注册量突破10万，获得市场积极反馈。' },
  { year: '2023 Q3', title: '百万用户', desc: '平台注册用户突破100万，上线社区互动功能，内容生态初步建立。' },
  { year: '2024 Q1', title: '功能升级', desc: '推出智能推荐系统和数据分析工具，用户体验大幅提升，月活用户突破300万。' },
  { year: '2024 Q4', title: '五百万用户', desc: '平台累计用户突破500万，推出高级会员服务，商业变现模式初步验证。' },
  { year: '2025 Q2', title: '生态完善', desc: '内容创作工具全面升级，社区活跃度创新高，合作伙伴超过2000家。' },
  { year: '2025 Q4', title: '千万用户里程碑', desc: '累计注册用户突破1000万大关，跻身行业领先平台行列。' },
  { year: '2026 Q2', title: '全新升级', desc: '平台全面改版升级，AI智能助手上线，开启智能化发展新篇章。' },
];

const awards = [
  { icon: 'ri-trophy-line', title: '最佳用户体验奖', desc: '2024年度中国互联网平台评选' },
  { icon: 'ri-medal-line', title: '创新技术应用奖', desc: '2025年数字科技创新峰会' },
  { icon: 'ri-star-smile-line', title: '用户满意度五星认证', desc: '连续三年获得用户满意度最高评级' },
  { icon: 'ri-building-2-line', title: '行业领军企业', desc: '2025年度数字经济行业评选' },
];

export default function AboutPage() {
  return (
    <>
      <SEOMeta
        title="关于我们 - 成91平台 | 品牌介绍、声誉与联系方式"
        description="了解成91平台的品牌故事、发展历程、行业声誉和联系方式。成91是备受关注的综合性热门平台，致力于为用户提供优质的数字化服务体验。"
        keywords="成91,关于成91,成91联系方式,成91品牌,成91平台介绍"
        canonicalUrl="https://cheng91.com/about"
        ogTitle="关于我们 - 成91平台"
        ogDescription="了解成91平台的品牌故事、发展历程、行业声誉和联系方式。"
        ogType="website"
      />

      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          {/* Page Title */}
          <div className="text-center mb-14">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              关于成91平台
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              从创立到行业领先，成91始终坚持以用户为中心，持续创新。
            </p>
          </div>

          {/* Brand Introduction */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  成91是一个备受关注的综合性热门平台，致力于为用户提供优质、便捷的数字化服务。我们汇聚了丰富的资源和专业的服务团队，通过持续创新和技术升级，为用户打造一流的平台体验。
                </p>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  平台以用户需求为导向，不断完善功能、提升服务品质。从智能推荐到数据分析，从社区互动到内容创作，成91提供了全方位的数字服务解决方案，满足不同用户群体的多样化需求。
                </p>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  我们的使命是让每一位用户都能在成91平台上找到价值，无论是获取信息、学习知识、交流互动还是实现商业目标，成91都是您值得信赖的伙伴。
                </p>
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20space%20with%20warm%20coral%20and%20teal%20accents%2C%20collaborative%20team%20working%20on%20digital%20products%2C%20clean%20bright%20environment%2C%20soft%20natural%20lighting%2C%20professional%20creative%20atmosphere%2C%20harmonious%20composition&width=800&height=600&seq=about-brand-cheng91&orientation=landscape"
                  alt="成91平台品牌"
                  title="成91平台品牌介绍"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Development Timeline */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 transform md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((item, idx) => (
                  <div
                    key={idx}
                    className={`relative flex flex-col md:flex-row gap-4 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full transform -translate-x-1.5 mt-1.5 z-10 ring-4 ring-background-50"></div>

                    {/* Content */}
                    <div className={`ml-10 md:ml-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-md text-xs font-semibold mb-2">
                        {item.year}
                      </span>
                      <h4 className="font-heading font-semibold text-foreground-950 mb-1">{item.title}</h4>
                      <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                    {/* Spacer for the other side */}
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Awards & Reputation */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              平台声誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {awards.map((award, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
                >
                  <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-accent-100 rounded-full">
                    <i className={`${award.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <h4 className="font-heading font-semibold text-foreground-950 mb-2">{award.title}</h4>
                  <p className="text-foreground-500 text-xs">{award.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-16" id="contact">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              联系方式
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-primary-100 rounded-full">
                  <i className="ri-phone-line text-xl text-primary-600"></i>
                </div>
                <h4 className="font-heading font-semibold text-foreground-950 mb-2">客服热线</h4>
                <a href="tel:4001234567" className="text-primary-600 text-sm hover:text-primary-700 transition-colors cursor-pointer">
                  400-123-4567
                </a>
                <p className="text-foreground-500 text-xs mt-1">7x24小时全天候服务</p>
              </div>

              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-primary-100 rounded-full">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h4 className="font-heading font-semibold text-foreground-950 mb-2">电子邮箱</h4>
                <a href="mailto:support@cheng91.com" className="text-primary-600 text-sm hover:text-primary-700 transition-colors cursor-pointer">
                  support@cheng91.com
                </a>
                <p className="text-foreground-500 text-xs mt-1">工作日24小时内回复</p>
              </div>

              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-primary-100 rounded-full">
                  <i className="ri-map-pin-line text-xl text-primary-600"></i>
                </div>
                <h4 className="font-heading font-semibold text-foreground-950 mb-2">公司地址</h4>
                <p className="text-foreground-600 text-sm">北京市朝阳区科技园路88号</p>
                <p className="text-foreground-500 text-xs mt-1">成91大厦 18层</p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="mb-16">
            <div className="bg-accent-500 rounded-2xl p-10 md:p-14 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 dark:text-foreground-950 mb-3">
                准备好开启成91之旅了吗？
              </h2>
              <p className="text-background-50/90 dark:text-foreground-950/90 text-sm mb-6 max-w-md mx-auto">
                加入千万用户的选择，体验成91平台带来的全新数字化体验
              </p>
              <a
                href="/"
                className="inline-flex items-center px-8 py-3 bg-background-50 text-foreground-950 rounded-lg font-medium text-sm hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                立即开始
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}